import React, { useContext, useMemo, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { AuthContext } from "../../context/authContext";
import registerImg from "../../assets/all-images/bg10.jpg";
import "../../styles/product-details.css";

const benefits = [
  "Expedited checkout and secure delivery",
  "Personalized dashboard for orders",
  "Manage custom jewellery requests",
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// const hasGoogleClientId =
//   !!googleClientId && googleClientId !== "YOUR_GOOGLE_CLIENT_ID";

const Register = () => {
  const { register, loginWithGoogle } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordChecks = useMemo(
    () => ({
      length: formData.password.length >= 8,
      number: /\d/.test(formData.password),
      letter: /[a-zA-Z]/.test(formData.password),
    }),
    [formData.password]
  );

  const formIsValid =
    formData.full_name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) &&
    formData.phone_number.trim().length >= 7 &&
    passwordChecks.length &&
    passwordChecks.number &&
    passwordChecks.letter;

  const goAfterAuth = (role) => {
    navigate(role === "admin" ? "/admin/dashboard" : "/");
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formIsValid) {
      setError("Complete all fields and use a stronger password to continue.");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await register({
        ...formData,
        email: formData.email.trim(),
      });
      setSuccess("Account created successfully.");
      goAfterAuth(response.role);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  // Removed Google login handler

  return (
    <main className="roha-auth-page">
      <div className="roha-auth-shell">
        <section className="roha-auth-showcase">
          <img
            src={registerImg}
            alt="Roha Jewellery registration experience"
            className="roha-auth-showcase__image"
          />
          <div className="roha-auth-showcase__overlay" />
          <div className="roha-auth-showcase__content">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <p className="roha-eyebrow">Create account</p>
              <h1>Register Your Account</h1>
              <p>
                Create your Roha Jewellery account to access exclusive features, manage your orders,
                and enjoy a seamless shopping experience for Ethiopian gemstones.
              </p>

              <div className="roha-auth-trust">
                {benefits.map((item) => (
                  <span key={item}>
                    <FaCheckCircle /> {item}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="roha-auth-card">
          <div className="roha-auth-card__inner">
            <div className="roha-auth-heading">
              <p className="roha-eyebrow">Roha Membership</p>
              <h2>Create Your Account</h2>
              <p className="roha-auth-card__intro">
                Register below to join Ethiopia's leading gemstone community and enjoy exclusive
                access to rare stones and expert support.
              </p>
            </div>

            {error && <div className="roha-auth-alert roha-auth-alert--error">{error}</div>}
            {success && (
              <div className="roha-auth-alert roha-auth-alert--success">
                <FaCheckCircle /> {success}
              </div>
            )}

            <div className="roha-auth-divider">
              <span>create your account</span>
            </div>

            <form className="roha-auth-form" onSubmit={handleSubmit}>
              <label>
                <span>Full name</span>
                <input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
              </label>

              <label>
                <span>Email address</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </label>

              <label>
                <span>Phone number</span>
                <input
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="+251..."
                  autoComplete="tel"
                  required
                />
              </label>

              <label>
                <span>Password</span>
                <div className="roha-auth-password">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>

              <div className="roha-auth-checklist">
                <span className={passwordChecks.length ? "is-valid" : ""}>
                  {passwordChecks.length ? "✓" : "○"} 8+ characters
                </span>
                <span className={passwordChecks.letter ? "is-valid" : ""}>
                  {passwordChecks.letter ? "✓" : "○"} Contains a letter
                </span>
                <span className={passwordChecks.number ? "is-valid" : ""}>
                  {passwordChecks.number ? "✓" : "○"} Contains a number
                </span>
              </div>

              <button
                type="submit"
                className="roha-button roha-button--solid"
                disabled={loading || !formIsValid}
                style={{ marginTop: "1rem" }}
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <p className="roha-auth-footer">
              Already registered? <Link to="/login">Sign in here</Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Register;
